import { formatPesos } from '@dayon/money';
import type { ReconciliationCheck } from './scope-words.ts';

/**
 * The pure half of the SA reconciliation screen (SA-112, SA-125): the finding
 * `GET /api/games/provider-reconciliation/issues` answers, and how its reason
 * and detail read. Separate so it is testable without a DOM.
 */
export interface ReconciliationIssue {
  id: string;
  /** MISSING_HERE · MISSING_AT_PROVIDER · AMOUNT_MISMATCH · UNSUCCESSFUL_AT_PROVIDER */
  kind: string;
  txnId: string;
  /** SA's GameID — what ResendTransaction works by. Null when the finding names none. */
  providerGameId: string | null;
  playerRef: string | null;
  /** The reconciler's own evidence; its keys differ by kind. */
  detail: Record<string, unknown>;
  /** OPEN · RESOLVED */
  status: string;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Why a RESOLVED finding was closed, and by whom. Null while it is open. */
  resolutionReason: string | null;
  resolvedBy: string | null;
  /** The admin's display name, or null when that admin is gone. */
  resolvedByName: string | null;
  resolvedAt: string | null;
  /** What our own ledger did with the bet the finding names, read when the list was read. */
  ourSide: OurSide;
  /** The code we answered SA when ourSide is REFUSED; null otherwise. */
  ourRefusal: number | null;
}

export type OurSide =
  | 'UNKNOWN_BET'
  | 'NOT_RECEIVED'
  | 'REFUSED'
  | 'CANCELLED_BEFORE_BET'
  | 'REFUNDED'
  | 'WIN_PAID'
  | 'LOSS_RECORDED'
  | 'OPEN';

export interface ReconciliationPage {
  items: ReconciliationIssue[];
  nextCursor: string | null;
  /** True when the reader is restricted to some brands, and the findings are only theirs (SEC-002). */
  brandRestricted: boolean;
  /** Whether the provider is connected here, and when its check last finished a run (null: never). */
  check: ReconciliationCheck;
}

/** A resolve must say why (10..500 characters), like every other money action. */
export const RESOLVE_REASON_MIN = 10;
export const RESOLVE_REASON_MAX = 500;

export function resolveReasonError(reason: string): string | null {
  const text = reason.trim();
  if (text.length < RESOLVE_REASON_MIN)
    return `Say why in at least ${RESOLVE_REASON_MIN} characters — it goes in the audit trail.`;
  if (text.length > RESOLVE_REASON_MAX) return `At most ${RESOLVE_REASON_MAX} characters.`;
  return null;
}

/** The providers whose findings the screen can show. SA first: it is what the screen opens on. */
export const PROVIDER_OPTIONS: readonly { code: string; label: string }[] = [
  { code: 'SA', label: 'SA Gaming' },
  { code: 'JILI', label: 'JILI' },
];
export const DEFAULT_PROVIDER = 'SA';

export function providerLabel(code: string): string {
  return PROVIDER_OPTIONS.find((p) => p.code === code)?.label ?? code;
}

/**
 * What only SA has. "Our side" is read from SA's PlaceBet records, and Resend is SA's own
 * ResendTransaction call; for a JILI finding the server claims neither, so the screen shows neither.
 */
export const hidesOurSide = (provider: string): boolean => provider !== 'SA';
export const offersResend = (provider: string): boolean => provider === 'SA';

const KINDS_BY_PROVIDER: Record<string, readonly string[]> = {
  SA: ['MISSING_HERE', 'MISSING_AT_PROVIDER', 'AMOUNT_MISMATCH', 'UNSUCCESSFUL_AT_PROVIDER'],
  JILI: ['CANCEL_UNCOVERED', 'SETTLE_UNCOVERED', 'SETTLE_UNMATCHED', 'TOTALS_MISMATCH'],
};

/** The reasons a provider can record, for the Reason chips: SA's chips never offer JILI's reasons, nor the reverse. */
export const kindsFor = (provider: string): readonly string[] => KINDS_BY_PROVIDER[provider] ?? [];

export interface IssueFilters {
  /** SA or JILI: whose findings. Always sent, so the server never has to guess. */
  provider: string;
  status: string;
  /** '' for every reason; otherwise one ISSUE_KIND key, filtered in SQL. */
  kind: string;
  /** Manila calendar days, inclusive, on lastSeenAt. */
  from: string;
  to: string;
}

export const ANY_KIND = '';

/**
 * The issues query.
 *
 * The reason goes to the SERVER as `kind`. Filtering the loaded pages here
 * instead showed an empty table with Load more under it whenever the matches
 * were further down the keyset.
 */
export function issuesPath(filters: IssueFilters, cursor: string | null): string {
  const q = new URLSearchParams({ status: filters.status, limit: '100', provider: filters.provider });
  if (filters.kind) q.set('kind', filters.kind);
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (cursor) q.set('cursor', cursor);
  return `/api/games/provider-reconciliation/issues?${q.toString()}`;
}

/**
 * The resolve dialog's whole state, so there is ONE closed value and every
 * exit returns it — cancel, dismiss, success AND failure.
 *
 * A failed resolve used to leave the sentence in the box, so the next finding
 * opened with somebody else's explanation already typed, ready to be filed
 * against a different money mismatch with nothing in the record to show it was
 * carried over.
 */
export interface ResolveDialog<A> {
  action: A | null;
  reason: string;
  reasonTouched: boolean;
}

export function closedDialog<A>(): ResolveDialog<A> {
  return { action: null, reason: '', reasonTouched: false };
}

export function openDialog<A>(action: A): ResolveDialog<A> {
  return { action, reason: '', reasonTouched: false };
}

/**
 * Whether an answer still belongs to the filters on screen.
 *
 * A Load more in flight when the filter changes must not append the old
 * filter's page, nor leave its cursor behind to page on from.
 */
export function isCurrentGeneration(requested: number, current: number): boolean {
  return requested === current;
}

export const ISSUE_KIND: Record<string, string> = {
  MISSING_HERE: 'SA has it, we do not',
  MISSING_AT_PROVIDER: 'We have it, SA does not',
  AMOUNT_MISMATCH: 'Amounts differ',
  UNSUCCESSFUL_AT_PROVIDER: 'Unsuccessful at SA',
  /* JILI's. A cancel or a settle moves money back that the player may already have spent. */
  CANCEL_UNCOVERED: 'Cancel could not be covered',
  SETTLE_UNCOVERED: 'Settle could not be covered',
  SETTLE_UNMATCHED: 'Settle for a hand we never took',
  TOTALS_MISMATCH: 'Hourly totals differ',
};

/**
 * OUR SIDE, in words: what our own ledger did with the bet a finding names.
 *
 * A fact about our ledger, not a verdict on the finding. `owed` is true where a
 * person must look whatever the finding: a stake taken and not settled, or a bet
 * SA gave no number for. For the rest, whether anyone is owed depends on the
 * finding's kind — see `verdictFor`.
 */
export const OUR_SIDE: Record<OurSide, { label: string; tone: 'neutral' | 'quiet' | 'warn'; owed: boolean }> = {
  WIN_PAID: { label: 'Already settled on our side · win paid', tone: 'neutral', owed: false },
  LOSS_RECORDED: { label: 'Already settled on our side · loss recorded', tone: 'neutral', owed: false },
  REFUNDED: { label: 'Already settled on our side · stake refunded', tone: 'neutral', owed: false },
  REFUSED: { label: 'We refused this bet', tone: 'quiet', owed: false },
  CANCELLED_BEFORE_BET: { label: 'Cancelled before it reached us', tone: 'quiet', owed: false },
  NOT_RECEIVED: { label: 'No record of this bet here', tone: 'quiet', owed: false },
  /* The two a person must look at wear the attention tone, never the accent's green. */
  OPEN: { label: 'Stake taken, not settled — check this one', tone: 'warn', owed: true },
  UNKNOWN_BET: { label: 'SA gave no bet number — check SA’s statement', tone: 'warn', owed: true },
};

/** The code we answered SA with, in words (the seamless-wallet codes). */
const OUR_REFUSAL: Record<number, string> = {
  1000: 'player not recognised',
  1001: 'wrong currency',
  1002: 'invalid amount',
  1003: 'account barred, or over the player’s own limit',
  1004: 'not enough balance',
  1005: 'general refusal',
  9999: 'system error',
};

/** The whole "our side" sentence for a finding, the refusal's reason included. */
export function ourSideLabel(issue: Pick<ReconciliationIssue, 'ourSide' | 'ourRefusal'>): string {
  const base = OUR_SIDE[issue.ourSide]?.label ?? issue.ourSide;
  if (issue.ourSide !== 'REFUSED' || issue.ourRefusal === null) return base;
  return `${base} · ${Object.hasOwn(OUR_REFUSAL, issue.ourRefusal) ? OUR_REFUSAL[issue.ourRefusal] : `code ${issue.ourRefusal}`}`;
}

/**
 * WHAT THE DIALOGS SAY ABOUT WHETHER ANYONE CAN STILL BE OWED.
 *
 * "Nothing is owed, do not pay it by hand" is true only of a bet SA lists as
 * UNSUCCESSFUL that our ledger settled, refused or never took: SA lists those
 * when one reply of ours was lost (SA's test plan answers a repeat 1005, and
 * SA retries any non-zero for two hours), and lists every bet we refuse. On the
 * other kinds the two records disagree about the bet itself — an amount, or which
 * side holds it — so a settled bet here proves nothing, and the words say so.
 */
export function verdictFor(
  issue: Pick<ReconciliationIssue, 'kind' | 'ourSide' | 'ourRefusal'>,
): { tone: 'warn' | 'quiet'; text: string } {
  if (issue.ourSide === 'UNKNOWN_BET')
    return { tone: 'warn', text: 'SA gave no bet number, so our records cannot be checked. Check SA’s statement for this round before you close it.' };
  if (issue.kind !== 'UNSUCCESSFUL_AT_PROVIDER')
    return { tone: 'warn', text: 'SA’s records and ours disagree about this bet, so this label alone does not settle it. Compare it with SA’s statement before you close it.' };
  if (issue.ourSide === 'OPEN')
    return { tone: 'warn', text: 'The stake was taken and nothing has settled it yet. Check SA’s statement for this round, and the player’s ledger for a payment made by hand, before you close it.' };
  if (issue.ourSide === 'WIN_PAID' || issue.ourSide === 'REFUNDED')
    return { tone: 'quiet', text: 'Our ledger owes nothing more on this bet. Do not pay it by hand: that would pay it twice.' };
  return { tone: 'quiet', text: 'Our ledger owes nothing on this bet. Do not pay it by hand: nothing is owed.' };
}

/**
 * The example in the resolve box, matched to the finding: an example that says
 * "the bet never reached us" over a stake we took would put the wrong
 * explanation in the audit trail one keystroke at a time.
 */
export function resolvePlaceholder(issue: Pick<ReconciliationIssue, 'kind' | 'ourSide'>): string {
  if (issue.kind !== 'UNSUCCESSFUL_AT_PROVIDER' || issue.ourSide === 'UNKNOWN_BET')
    return 'Compared with SA’s statement: what each side shows, and what was done about it.';
  switch (issue.ourSide) {
    case 'WIN_PAID':
    case 'LOSS_RECORDED':
    case 'REFUNDED':
      return 'Our ledger had already settled this bet; SA listed it after one of our replies was lost. Nothing is owed.';
    case 'REFUSED':
      return 'We refused this bet, so no stake was taken. Nothing is owed.';
    case 'OPEN':
      return 'Checked SA’s statement for this round: what it shows, and what was done about it.';
    default:
      return 'Checked against SA’s statement; there is no record of this bet here and the player was not debited.';
  }
}

/**
 * A finding that is open again after somebody closed it: the job reopens one SA
 * still lists, and keeps who closed it and why. Said on the row, because "our
 * side" cannot see money paid by hand, and the person who closed it may have
 * paid it.
 */
export function closedBeforeLine(
  issue: Pick<ReconciliationIssue, 'status' | 'resolvedAt' | 'resolvedByName' | 'resolvedBy' | 'resolutionReason'>,
  formatWhen: (iso: string) => string,
  provider = 'SA',
): string | null {
  if (issue.status !== 'OPEN' || issue.resolvedAt === null) return null;
  const who = issue.resolvedByName ?? issue.resolvedBy ?? 'someone';
  return `Closed before by ${who} on ${formatWhen(issue.resolvedAt)}: ${issue.resolutionReason ?? 'no reason recorded'}. ${provider === 'SA' ? 'SA still lists it' : 'It is still found'}.`;
}

/**
 * SA's Unsuccessful Reason Codes (SA API document §7.2, saapiservice.com/api-document,
 * read 2026-10-26; it lists no 144), so a finding reads
 * "Insufficient balance (142)" rather than a bare number. 138 to 145 are SA
 * recording OUR answer: 142 is our 1004, 141 our 1003, 143 our 1005.
 */
export const SA_UNSUCCESSFUL_REASON: Record<string, string> = {
  '5': 'Bet timeout',
  '6': 'Insufficient balance',
  '11': 'Betting failed',
  '12': 'Exceeded maximum bet limit',
  '14': 'Below minimum bet limit',
  '114': 'Seamless wallet request timeout',
  '138': 'Player not found',
  '139': 'Invalid currency',
  '140': 'Invalid amount',
  '141': 'Account locked',
  '142': 'Insufficient balance',
  '143': 'General error',
  '145': 'System error',
  '166': 'Player reached maximum winning amount',
  '219': 'Opposite bet type offset fail',
};

/**
 * The detail object as short "key: value" lines. Nulls are dropped — an empty
 * field is not evidence — and anything nested is written out as JSON rather
 * than flattened into a guess. SA's reason code is written in SA's words, and
 * `takenHere` is left out: it is the job's reading at its last run (up to ten
 * minutes old), whether our PlaceBet stood then, and "our side" says that and
 * more as it is now.
 */
export function detailLines(detail: Record<string, unknown>): string[] {
  return Object.entries(detail)
    .filter(([key, value]) => value !== null && value !== undefined && key !== 'takenHere')
    .map(([key, value]) => {
      const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
      if (key === 'reason' && Object.hasOwn(SA_UNSUCCESSFUL_REASON, text))
        return `SA’s reason: ${SA_UNSUCCESSFUL_REASON[text]} (${text})`;
      return `${key}: ${text.length > 120 ? `${text.slice(0, 117)}…` : text}`;
    });
}

/** SA's own decimal pesos ("60", "60.5", "60.00") as centavos, by SA's amount rule; null when it is not one. */
const saPesos = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const m = /^(-)?(\d{1,15})(?:\.(\d{1,2}))?$/.exec(String(value).trim());
  if (!m) return null;
  const centavos = BigInt(m[2]!) * 100n + BigInt((m[3] ?? '').padEnd(2, '0'));
  return formatPesos(m[1] ? -centavos : centavos);
};

/** SA's clock is GMT+8, Manila time, and its report times carry no zone: read them as such. */
const saTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})$/.exec(value.trim());
  if (!m) return null;
  const at = Date.parse(`${m[1]}T${m[2]}+08:00`);
  return Number.isNaN(at) ? null : new Date(at).toISOString();
};

/** A value SA sent that this cannot read, passed on as SA wrote it. */
const asWritten = (label: string, v: unknown) => `${label}, as SA wrote it: ${String(v)}`;

/**
 * AN SA FINDING'S EVIDENCE IN PLAIN WORDS AND PESOS.
 *
 * The Detail column printed the check's own field names and an amount in centavos ("bets: 1 |
 * theirAmount: 50000", read as fifty thousand; module walk, 30 Oct 2026). Every key the SA check
 * writes (services/worker reconcile-sa) is said in words here:
 *
 *   theirAmount, ourAmount   centavos, as the check stores them; null when the amount could not be read
 *   bets                     how many of SA's bets share the transaction
 *   window                   the stretch a bet SA does not have was looked for in
 *   utid, reason, betAmount, betTime   SA's own record of an unsuccessful bet: its reference, its
 *                            reason code, its amount in SA's decimal pesos, its time on SA's clock
 *
 * Left out: ourStatus and takenHere, the check's reading of our side at its last run — the Our side
 * column says that, as it is now — and transactionId, which is the row's own Txn ID. A key this does
 * not know is written as it was stored, so nothing is dropped. Times go through `formatWhen`, the
 * screen's own formatter.
 */
export function saDetailLines(detail: Record<string, unknown>, formatWhen: (iso: string) => string): string[] {
  const known: Record<string, (v: unknown) => string | null> = {
    theirAmount: (v) => {
      const t = v === null ? null : centavosText(v);
      return t === null ? 'SA’s amount could not be read' : `SA says: ${t}`;
    },
    ourAmount: (v) => {
      const t = v === null ? null : centavosText(v);
      return t === null ? 'Our amount was not recorded' : `We took: ${t}`;
    },
    bets: (v) =>
      typeof v === 'number' ? `SA lists ${v} bet${v === 1 ? '' : 's'} under this transaction` : null,
    window: (v) => {
      if (typeof v !== 'object' || v === null) return null;
      const { from, to } = v as { from?: unknown; to?: unknown };
      if (typeof from !== 'string' || typeof to !== 'string' || Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to)))
        return null;
      return `Looked for at SA between ${formatWhen(from)} and ${formatWhen(to)}`;
    },
    utid: (v) => `SA’s reference: ${String(v)}`,
    reason: (v) => {
      const code = String(v);
      return Object.hasOwn(SA_UNSUCCESSFUL_REASON, code)
        ? `SA’s reason: ${SA_UNSUCCESSFUL_REASON[code]} (${code})`
        : `SA’s reason code: ${code}`;
    },
    betAmount: (v) => {
      const t = saPesos(v);
      return t === null ? asWritten('Bet', v) : `Bet: ${t}`;
    },
    betTime: (v) => {
      const at = saTime(v);
      return at === null ? asWritten('Bet time', v) : `Bet placed at ${formatWhen(at)}`;
    },
  };
  const leftOut = new Set(['ourStatus', 'takenHere', 'transactionId']);
  return Object.entries(detail).flatMap(([key, value]) => {
    if (leftOut.has(key) || value === undefined) return [];
    /* A null amount is evidence (it could not be read); any other null is not. */
    if (value === null && key !== 'theirAmount' && key !== 'ourAmount') return [];
    const line = Object.hasOwn(known, key) ? known[key]!(value) : null;
    return line !== null ? [line] : detailLines({ [key]: value });
  });
}

/** What each of JILI's reasons means, one sentence, for the explanation under a finding's dialog. */
export const JILI_KIND_MEANING: Record<string, string> = {
  CANCEL_UNCOVERED: 'JILI cancelled a bet whose win the player had already spent, so we could not take the win back.',
  SETTLE_UNCOVERED: 'A table session’s settle needed more money than the player had.',
  SETTLE_UNMATCHED: 'JILI settled a session in which we took no hand.',
  TOTALS_MISMATCH: 'JILI’s totals for an hour of one game differ from ours. Nothing was corrected.',
};

const centavosText = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  try {
    return formatPesos(BigInt(value));
  } catch {
    return null;
  }
};

/** The instant the finding's hour starts, or null when the window holds no readable one. */
const hourStart = (window: unknown): string | null => {
  if (typeof window !== 'object' || window === null) return null;
  const from = (window as { from?: unknown }).from;
  return typeof from === 'string' && !Number.isNaN(Date.parse(from)) ? from : null;
};

/** One side of an hourly totals finding: "bet ₱5.00, paid out ₱4.00, 3 wagers". */
function totalsSide(side: unknown): string | null {
  if (typeof side !== 'object' || side === null) return null;
  const t = side as Record<string, unknown>;
  if (t['unreadable'] === true) return 'an amount we could not read';
  const bet = centavosText(t['bet']);
  const payoff = centavosText(t['payoff']);
  if (bet === null || payoff === null) return null;
  const count = typeof t['count'] === 'number' ? `, ${t['count']} wager${t['count'] === 1 ? '' : 's'}` : '';
  return `bet ${bet}, paid out ${payoff}${count}`;
}

/**
 * A JILI finding's evidence in plain words. The amounts are centavos on the wire and pesos here;
 * a key this does not know is written the way SA's are, so nothing is dropped. The hour's start
 * goes through `formatWhen`, the formatter the screen's When column uses, so it reads in Manila
 * time like every other time on the screen.
 */
export function jiliDetailLines(detail: Record<string, unknown>, formatWhen: (iso: string) => string): string[] {
  const known: Record<string, (v: unknown) => string | null> = {
    window: (v) => {
      const start = hourStart(v);
      return start === null ? null : `Hour starting: ${formatWhen(start)}`;
    },
    gameId: (v) => (v === null || v === undefined ? 'Game: all games' : `Game: ${String(v)}`),
    currency: (v) => `JILI’s currency: ${String(v)}`,
    reason: (v) =>
      v === 'CURRENCY_NOT_PHP' ? 'Not compared: JILI reported this hour in a currency other than pesos' : null,
    theirs: (v) => {
      const t = totalsSide(v);
      return t === null ? null : `JILI says: ${t}`;
    },
    ours: (v) => {
      const t = totalsSide(v);
      return t === null ? null : `We have: ${t}`;
    },
    bet: (v) => {
      const t = centavosText(v);
      return t === null ? null : `Bet: ${t}`;
    },
    win: (v) => {
      const t = centavosText(v);
      return t === null ? null : `Win: ${t}`;
    },
    holds: (v) => {
      const t = centavosText(v);
      return t === null ? null : `Held for the session: ${t}`;
    },
    balance: (v) => {
      const t = centavosText(v);
      return t === null ? null : `Player had: ${t}`;
    },
  };
  return Object.entries(detail).flatMap(([key, value]) => {
    /* A null is no evidence, except a null game id, which says the total is over every game. */
    if (value === undefined || (value === null && key !== 'gameId')) return [];
    const line = Object.hasOwn(known, key) ? known[key]!(value) : null;
    return line !== null ? [line] : detailLines({ [key]: value });
  });
}
